import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth-store';
import { readSidebarCookie, type SidebarState } from '@/lib/sidebar-cookie';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { AssistantWidget } from '@/components/assistant/assistant-widget';
import { TourProvider } from '@/lib/tour/tour-provider';
import { WelcomeModal } from '@/components/onboarding/welcome-modal';
import { cn } from '@/lib/utils';

export function DashboardShell() {
  const user = useAuthStore((s) => s.user);
  const [sidebarState, setSidebarState] = useState<SidebarState>(() =>
    readSidebarCookie(),
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const collapsed = sidebarState === 'collapsed';
  const sidebarWidth = collapsed ? 'md:ml-16' : 'md:ml-60';
  // Mesma escala numerica do Tailwind usada em w-16/w-60 (aside) e
  // ml-16/ml-60 (coluna de conteudo) — 64px/240px, garantindo que o canto
  // fixo trave exatamente na borda real da sidebar em qualquer estado.
  const cornerLeft = collapsed ? 'left-16' : 'left-60';

  return (
    <TourProvider>
      {/*
        Raiz SEM bg/blur/transform proprio — armadilha ja documentada:
        backdrop-filter (ou transform/perspective/will-change) num
        ancestral vira "containing block" de position:fixed/absolute,
        entao qualquer elemento fixed daqui pra baixo (sidebar, o canto
        fixo abaixo, SearchModal) ficaria preso ao box da RAIZ em vez do
        viewport. Fundo do chrome mora so em sidebar/topbar (pintura
        propria) e no canto fixo abaixo — nunca num ancestral comum.
      */}
      <div className="min-h-screen">
        <Sidebar
          initialState={sidebarState}
          mobileOpen={mobileOpen}
          onCloseMobile={() => setMobileOpen(false)}
          onToggle={(s) => setSidebarState(s)}
        />
        <div
          className={`flex min-h-screen flex-col transition-[margin-left] duration-200 ease-out ${sidebarWidth}`}
        >
          <Topbar
            userName={user?.name}
            userEmail={user?.email}
            onOpenMobileMenu={() => setMobileOpen(true)}
          />
          {/*
            Painel de conteudo: SEM canto arredondado proprio (ver o
            "canto fixo" abaixo — a curva agora e inteiramente dele).
            O background aqui replica exatamente a formula do body em
            globals.css (--bg + --bg-image + attachment fixed) pra o
            painel parecer uma "janela" pro fundo da pagina (inclusive as
            nuvens/blobs de cada tema).
          */}
          <main
            className="flex-1 px-4 py-6 md:px-8 md:py-8"
            style={{
              background: 'var(--bg)',
              backgroundImage: 'var(--bg-image)',
              backgroundAttachment: 'fixed',
            }}
          >
            <Outlet />
          </main>
        </div>

        {/*
          Dobra fixa (2026-08-06, 2a tentativa): a 1a versao arredondava o
          canto do <main> + um notch que rolavam junto com a pagina —
          assim que a pagina rolava um pixel, a "dobra" virava um canto
          reto (o <main> ja tinha saido da posicao onde a curva fazia
          sentido). Esta versao INVERTE o mecanismo: em vez de arredondar
          o conteudo, um elemento pequeno FIXO no viewport (nao rola,
          nunca) fica sempre plantado na junção real (borda da sidebar x
          base do topbar) e "recorta" visualmente o canto por cima de
          tudo. Pinta a cor do chrome (--surface, mesma declaracao de
          sidebar/topbar — sem empilhar) e usa mask-image (radial-gradient
          circular, raio 16px = mesmo raio visual da tentativa anterior)
          pra apagar o quarto de circulo voltado pro conteudo, deixando so
          o "cantinho" solido visivel — o resultado de fora e um canto
          arredondado sempre no mesmo lugar da tela, pagina parada ou
          rolada.

          Por que sem backdrop-blur aqui: mask-image + backdrop-filter tem
          suporte real em browsers modernos, mas e uma combinacao menos
          testada/mais propensa a bug de renderizacao em algum browser.
          A area e de 16px e nao ha nenhuma borda dura por tras que o blur
          precise suavizar (e so o fundo continuo da pagina) — a cor solida
          do --surface sem blur fica imperceptivel da versao com blur.
          Fallback deliberado, nao descuido.

          Escondido abaixo de md: no mobile a sidebar e overlay (nao ocupa
          espaco fixo do lado do conteudo), entao nao ha uma "junção" fixa
          real pra marcar ali.
        */}
        <div
          aria-hidden
          className={cn(
            'pointer-events-none fixed top-16 z-40 hidden h-4 w-4 bg-[var(--surface)]',
            'transition-[left] duration-200 ease-out md:block',
            cornerLeft,
          )}
          style={{
            maskImage:
              'radial-gradient(circle 16px at 100% 100%, transparent 16px, black 16px)',
            WebkitMaskImage:
              'radial-gradient(circle 16px at 100% 100%, transparent 16px, black 16px)',
          }}
        />

        <AssistantWidget />
        <WelcomeModal />
      </div>
    </TourProvider>
  );
}
